import { Button } from '@/ui/Button';
import { estadoDe } from '@/lib/api';

/**
 * EstadoError — dice qué falló y qué hacer, no repite el mensaje del servidor.
 *
 * Porta el criterio de `Utils.errorState()`, que es lo mejor que tiene el
 * portal vanilla en manejo de errores: **distingue por código HTTP**, porque
 * los tres casos necesitan salidas distintas y ofrecer «Reintentar» en los tres
 * es mentir en dos de ellos.
 *
 *   403 — no es un fallo, es que esto no está en tu rol. Reintentar no arregla
 *         nada; lo que sirve es volver e ir a otro sitio.
 *   404 — lo que buscabas ya no está. Reintentar tampoco sirve.
 *   otro / sin código — el servidor falló o no llegamos a preguntar.
 *         Reintentar SÍ suele funcionar.
 *
 * Un `err` sin `status` es un fallo de red o una excepción de JavaScript: no
 * llegamos a preguntar, así que se trata como fallo temporal.
 */
export function EstadoError({
  err, que = 'la información', onReintentar, onVolver,
}: {
  err: unknown;
  /** Qué se estaba cargando, en palabras del usuario: «tus solicitudes»,
   *  «esta pantalla». Sale en la frase, así que va en minúscula. */
  que?: string;
  onReintentar?: () => void;
  onVolver?: () => void;
}) {
  const codigo = estadoDe(err);
  const tecnico = err instanceof Error ? err.message : String(err ?? '');

  const sinPermiso = codigo === 403;
  const noExiste = codigo === 404;

  const titulo = sinPermiso
    ? 'Esto no está en tu rol'
    : noExiste
      ? `No encontramos ${que}`
      : `No pudimos cargar ${que}`;

  const explicacion = sinPermiso
    ? 'Tu usuario no tiene acceso a esta sección. Si crees que debería tenerlo, escríbele al área de Bienestar.'
    : noExiste
      ? 'Puede que se haya eliminado o que el enlace esté desactualizado.'
      : 'Suele ser algo temporal del servidor. Vuelve a intentarlo en unos segundos.';

  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <i
        className={`bi ${sinPermiso ? 'bi-shield-lock' : noExiste ? 'bi-search' : 'bi-exclamation-triangle'} text-9 text-muted opacity-60`}
        aria-hidden="true"
      />
      {/* `role="alert"` solo en el título: si envolviera el bloque entero, el
          lector leería también el detalle técnico, que no es para el usuario. */}
      <p className="text-5 font-semibold text-ink" role="alert">{titulo}</p>
      <p className="max-w-prose text-3 leading-relaxed text-muted">{explicacion}</p>

      <div className="mt-1 flex flex-wrap justify-center gap-2">
        {/* Reintentar SOLO cuando reintentar puede funcionar. */}
        {!sinPermiso && !noExiste && onReintentar && (
          <Button variant="primary" onClick={onReintentar}>Reintentar</Button>
        )}
        {onVolver && (
          <Button variant="ghost" onClick={onVolver}>Volver</Button>
        )}
      </div>

      {/* El detalle técnico queda accesible para reportarlo, plegado y en
          pequeño: quien lo necesita sabe buscarlo y a quien no, no le estorba. */}
      {tecnico && (
        <details className="mt-2 max-w-prose text-left">
          <summary className="cursor-pointer text-2 text-muted">Detalle técnico</summary>
          <code className="mt-1 block break-words rounded-xs bg-sunken p-2 text-1 text-ink-2">
            {codigo ? `${codigo} · ` : ''}{tecnico}
          </code>
        </details>
      )}
    </div>
  );
}
